import { CheckCircleIcon, PaperPlaneTiltIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup } from '@/components/ui/radio-group';
import { RadioOptionCard } from '@/components/ui/radio-option-card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateApprovalRequest } from '@/hooks/approvals/useApprovals';
import { useDeskMembers } from '@/hooks/desk/useDeskMembers';
import {
  articleHandoffKey,
  useArticleHandoffState,
  useUpdateArticleHandoff,
} from '@/hooks/reach/useArticleHandoff';
import { useReachEvents } from '@/hooks/reach/useReachEvents';
import { snapshotFromArticle } from '@/lib/approvals/snapshot';
import {
  PUBLICATION_STAGE_LABEL,
  PUBLICATION_WORKING_STAGES,
  type PublicationWorkingStage,
} from '@/lib/content-doctrine/article-publication-stage';
import { externalHandoffGate } from '@/lib/content-doctrine/article-release';
import { hashContent } from '@/lib/knowledge/extract-document';
import { withoutArchivedEvents } from '@/lib/reach/event-archive';
import { FIELD_LABEL } from '@/lib/ui-tokens';

type Route = 'desk' | 'external' | 'published';
type ReviewExemption = 'approved_outside_mora' | 'client_review_not_required';

export interface ArticleHandoffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  taskId: string;
  title: string;
  markdown: string;
  messageId: string;
  documentId: string;
  contentHash: string;
  canRelease: boolean;
  releaseDisabledReason?: string | null;
}

const toDate = (value: string | null | undefined): Date | undefined =>
  value ? new Date(value) : undefined;

export function ArticleHandoffDialog({
  open,
  onOpenChange,
  workspaceId,
  taskId,
  title,
  markdown,
  messageId,
  documentId,
  contentHash,
  canRelease,
  releaseDisabledReason = null,
}: ArticleHandoffDialogProps): JSX.Element {
  const [route, setRoute] = React.useState<Route>('desk');
  const [eventId, setEventId] = React.useState('none');
  const [publishDate, setPublishDate] = React.useState<Date | undefined>();
  const [publishedUrl, setPublishedUrl] = React.useState('');
  const [stage, setStage] = React.useState<PublicationWorkingStage>('handed_off');
  const [reviewExemption, setReviewExemption] = React.useState<ReviewExemption>(
    'client_review_not_required',
  );
  const queryClient = useQueryClient();
  const createRequest = useCreateApprovalRequest();
  const updateHandoff = useUpdateArticleHandoff({
    workspaceId,
    taskId,
    messageId,
    documentId,
    contentHash,
  });
  const handoff = useArticleHandoffState(taskId, contentHash);
  const { data: members = [] } = useDeskMembers(workspaceId);
  const { events } = useReachEvents();

  React.useEffect(() => {
    if (!open) return;
    const current = handoff.data;
    setPublishDate(toDate(current?.scheduledAt));
    setPublishedUrl(current?.publishedUrl ?? '');
    setStage(
      current?.publicationState &&
        (PUBLICATION_WORKING_STAGES as readonly string[]).includes(current.publicationState)
        ? (current.publicationState as PublicationWorkingStage)
        : 'handed_off',
    );
    if (current?.publishedAt) setRoute('published');
    else if (current?.publicationState) setRoute('external');
    else setRoute('desk');
  }, [handoff.data, open]);

  const busy = createRequest.isPending || updateHandoff.isPending;
  const reviewSatisfied =
    handoff.data?.reviewState === 'approved' || Boolean(handoff.data?.attestationReason);
  const externalGate = externalHandoffGate({
    releaseReady: canRelease,
    disabledReason: releaseDisabledReason,
    hasLivePublicationForThisRevision: Boolean(handoff.data?.publicationId),
  });
  const canSaveExternal = externalGate.allowed && (reviewSatisfied || Boolean(reviewExemption));
  const validPublishedUrl = /^https?:\/\/[^\s]+$/i.test(publishedUrl.trim());
  const needsDate = route === 'external' && stage === 'scheduled' && !publishDate;

  const submit = async (): Promise<void> => {
    try {
      if (route === 'desk') {
        const hash = contentHash || (await hashContent(markdown));
        const result = await createRequest.mutateAsync({
          workspaceId,
          projectId: handoff.data?.projectId ?? null,
          reachEventId: eventId === 'none' ? null : eventId,
          title: `${title} — article review`,
          note: null,
          items: [
            {
              itemKind: 'plan',
              title,
              projectTaskId: taskId,
              snapshot: snapshotFromArticle({
                articleRef: { messageId, documentId, contentHash: hash },
                markdown,
                title,
                scheduled_at: handoff.data?.scheduledAt ?? null,
              }),
            },
          ],
        });
        void queryClient.invalidateQueries({ queryKey: articleHandoffKey(taskId) });
        toast.success(
          result.deskMemberCount > 0
            ? 'Sent to the Content Desk'
            : 'Saved to the Content Desk — add a desk member so someone can review it',
        );
      } else {
        if (!canSaveExternal) throw new Error('Complete the article review before handoff.');
        if (route === 'published' && !validPublishedUrl) {
          throw new Error('Add the published article URL.');
        }
        if (needsDate) throw new Error('Choose the expected publish date.');
        await updateHandoff.mutateAsync({
          mode: route === 'published' ? 'published' : 'ready',
          ...(route === 'published' ? { publishedUrl: publishedUrl.trim() } : { stage }),
          scheduledDate: publishDate ? format(publishDate, 'yyyy-MM-dd') : null,
          approvalDecisionId: handoff.data?.approvalDecisionId ?? null,
          ...(!reviewSatisfied ? { reviewExemptionReason: reviewExemption } : {}),
        });
        toast.success(
          route === 'published' ? 'Marked published' : `${PUBLICATION_STAGE_LABEL[stage]} saved`,
        );
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update this handoff.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Hand off article</DialogTitle>
          <DialogDescription>
            Choose who reviews it and when it is expected to publish.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <RadioGroup value={route} onValueChange={(value) => setRoute(value as Route)}>
            <RadioOptionCard
              id="handoff-desk"
              value="desk"
              title="Send to Content Desk"
              description="Share this frozen revision with the people already in this workspace's desk."
              icon={PaperPlaneTiltIcon}
              selected={route === 'desk'}
            />
            <RadioOptionCard
              id="handoff-external"
              value="external"
              title="Pending external publishing"
              description="Track it with the third-party publisher through the SEO and internal check, adding images and scheduling."
              icon={UploadSimpleIcon}
              selected={route === 'external'}
              disabled={!externalGate.allowed}
              disabledReason={externalGate.reason ?? undefined}
            />
            <RadioOptionCard
              id="handoff-published"
              value="published"
              title="Published externally"
              description="Record the final public URL and completion time."
              icon={CheckCircleIcon}
              selected={route === 'published'}
              disabled={!externalGate.allowed}
              disabledReason={externalGate.reason ?? undefined}
            />
          </RadioGroup>

          {route === 'desk' ? (
            <div className="flex flex-col gap-3 border-t border-border/30 pt-4">
              <div className="flex flex-col gap-1.5">
                <Label className={FIELD_LABEL} htmlFor="handoff-event">
                  Related event{' '}
                  <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Select value={eventId} onValueChange={setEventId}>
                  <SelectTrigger id="handoff-event">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not event-specific</SelectItem>
                    {withoutArchivedEvents(events).map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-caption text-muted-foreground">
                {members.length > 0
                  ? `Goes to ${members.map((member) => member.name).join(', ')}.`
                  : 'This workspace has no Content Desk members yet. The review will be saved, but nobody can see it until a member is added.'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 border-t border-border/30 pt-4">
              {route === 'external' ? (
                <div className="flex flex-col gap-1.5">
                  <Label className={FIELD_LABEL} htmlFor="handoff-stage">
                    Stage
                  </Label>
                  <Select
                    value={stage}
                    onValueChange={(value) => setStage(value as PublicationWorkingStage)}
                  >
                    <SelectTrigger id="handoff-stage">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PUBLICATION_WORKING_STAGES.map((option) => (
                        <SelectItem key={option} value={option}>
                          {PUBLICATION_STAGE_LABEL[option]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              {!reviewSatisfied ? (
                <div className="flex flex-col gap-1.5">
                  <Label className={FIELD_LABEL} htmlFor="handoff-review-route">
                    Why no Content Desk review?
                  </Label>
                  <Select
                    value={reviewExemption}
                    onValueChange={(value) => setReviewExemption(value as ReviewExemption)}
                  >
                    <SelectTrigger id="handoff-review-route">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved_outside_mora">Approved outside Mora</SelectItem>
                      <SelectItem value="client_review_not_required">
                        Client review not required
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <div className="flex flex-col gap-1.5">
                <Label className={FIELD_LABEL} htmlFor="handoff-date">
                  Expected publish date{' '}
                  <span className="font-normal text-muted-foreground">
                    {route === 'external' && stage === 'scheduled' ? '(required)' : '(optional)'}
                  </span>
                </Label>
                <DatePicker
                  id="handoff-date"
                  value={publishDate}
                  onChange={setPublishDate}
                  placeholder="No date yet"
                  ariaLabel="Expected publish date"
                />
                <p className="text-detail text-muted-foreground">
                  A date places this article on the Publishing calendar. It remains pending until
                  someone records the external publication.
                </p>
              </div>
              {route === 'published' ? (
                <div className="flex flex-col gap-1.5">
                  <Label className={FIELD_LABEL} htmlFor="handoff-published-url">
                    Published URL
                  </Label>
                  <Input
                    id="handoff-published-url"
                    value={publishedUrl}
                    onChange={(event) => setPublishedUrl(event.target.value)}
                    placeholder="https://example.com/article"
                    inputMode="url"
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>

        <DialogFooter>
          {handoff.data?.publicationState ? (
            <Button
              type="button"
              variant="flat"
              disabled={busy}
              onClick={() =>
                void updateHandoff.mutateAsync({ mode: 'clear' }).then(() => {
                  toast.success('Publishing handoff cleared');
                  onOpenChange(false);
                })
              }
            >
              Clear publishing status
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="neutral"
            disabled={
              busy ||
              (route !== 'desk' && !canSaveExternal) ||
              (route === 'published' && !validPublishedUrl) ||
              needsDate
            }
            onClick={() => void submit()}
          >
            {route === 'desk'
              ? 'Send to Content Desk'
              : route === 'published'
                ? 'Mark published'
                : handoff.data?.publicationState
                  ? 'Update stage'
                  : 'Save hand-off'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
